import { gatewayApi as api } from './gatewayApi';

// Interfaces
export interface User {
  Id: number;
  Email: string;
  FirstName: string;
  LastName: string;
  Phone?: string;
  CompanyName?: string;
  Active: boolean;
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface Subscription {
  Id: number;
  UserId: number;
  PlanName: string;
  Status: string; // 'active', 'cancelled', 'expired', 'past_due'
  BillingCycle: string; // 'monthly', 'yearly'
  Amount: number;
  Currency: string;
  StartDate: string;
  EndDate?: string;
  NextBillingDate?: string;
  CancelledDate?: string;
  TrialEndDate?: string;
  PaymentMethodId?: string;
  Active: boolean;
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface PaymentMethod {
  Id: string;
  Type: string; // 'credit_card', 'bank_transfer', 'paypal'
  CardNumber?: string; // masked, e.g., "**** **** **** 1234"
  CardBrand?: string; // 'visa', 'mastercard', 'amex'
  ExpirationMonth?: number;
  ExpirationYear?: number;
  CardholderName?: string;
  BillingAddress?: {
    Street: string;
    City: string;
    State: string;
    PostalCode: string;
    Country: string;
  };
  IsDefault: boolean;
  Active: boolean;
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface Invoice {
  Id: string;
  SubscriptionId: number;
  InvoiceNumber: string;
  Amount: number;
  Currency: string;
  Status: string; // 'paid', 'pending', 'failed', 'cancelled'
  IssueDate: string;
  DueDate: string;
  PaidDate?: string;
  Description: string;
  DownloadUrl?: string;
  Active: boolean;
}

export interface BillingHistory {
  invoices: Invoice[];
  total: number;
}

export interface AccountInfo {
  user: User;
  subscription: Subscription | null;
  paymentMethods: PaymentMethod[];
  defaultPaymentMethod: PaymentMethod | null;
}

export interface UpdatePaymentMethodRequest {
  CardNumber: string;
  ExpirationMonth: number;
  ExpirationYear: number;
  CVC: string;
  CardholderName?: string;
  BillingAddress?: {
    Street: string;
    City: string;
    State: string;
    PostalCode: string;
    Country: string;
  };
}

export interface ChangeEmailRequest {
  NewEmail: string;
  CurrentPassword: string;
}

export interface ChangePasswordRequest {
  CurrentPassword: string;
  NewPassword: string;
  ConfirmPassword: string;
}

export interface CancelSubscriptionResponse {
  success: boolean;
  message: string;
  effectiveDate?: string;
}

export interface UpdatePaymentMethodResponse {
  success: boolean;
  message: string;
  paymentMethod?: PaymentMethod;
}

// API Service
export const accountApi = {
  // Account Information
  getAccountInfo: (): Promise<AccountInfo> =>
    api.get('/account'),

  getUserProfile: (): Promise<User> =>
    api.get('/account/profile'),

  updateUserProfile: (user: Partial<User>): Promise<User> =>
    api.put('/account/profile', user),

  // Email and Password Management
  changeEmail: (request: ChangeEmailRequest): Promise<{ success: boolean; message: string }> =>
    api.post('/account/change-email', request),

  changePassword: (request: ChangePasswordRequest): Promise<{ success: boolean; message: string }> =>
    api.post('/account/change-password', request),

  // Subscription Management
  getSubscription: (): Promise<Subscription | null> =>
    api.get('/subscription'),

  cancelSubscription: (): Promise<CancelSubscriptionResponse> =>
    api.post('/subscription/cancel'),

  reactivateSubscription: (): Promise<{ success: boolean; message: string }> =>
    api.post('/subscription/reactivate'),

  // Payment Method Management
  getPaymentMethods: (): Promise<PaymentMethod[]> =>
    api.get('/account/payment-methods'),

  getDefaultPaymentMethod: (): Promise<PaymentMethod | null> =>
    api.get('/account/payment-methods/default'),

  updatePaymentMethod: (request: UpdatePaymentMethodRequest): Promise<UpdatePaymentMethodResponse> =>
    api.post('/subscription/changepaymentmethod', { data: request }),

  addPaymentMethod: (request: UpdatePaymentMethodRequest): Promise<PaymentMethod> =>
    api.post('/account/payment-methods', request),

  deletePaymentMethod: (paymentMethodId: string): Promise<{ success: boolean; message: string }> =>
    api.delete(`/account/payment-methods/${paymentMethodId}`),

  setDefaultPaymentMethod: (paymentMethodId: string): Promise<{ success: boolean; message: string }> =>
    api.put(`/account/payment-methods/${paymentMethodId}/default`),

  // Billing and Invoices
  getBillingHistory: (params?: { page?: number; limit?: number }): Promise<BillingHistory> =>
    api.get('/account/billing-history', params as any),

  downloadInvoice: (invoiceId: string): Promise<Blob> =>
    api.getBlob(`/account/invoices/${invoiceId}/download`),

  // Account Actions
  closeAccount: (reason?: string): Promise<{ success: boolean; message: string }> =>
    api.post('/account/close', { reason }),

  // Validation helpers
  validation: {
    validateEmail: (email: string): string[] => {
      const errors: string[] = [];
      
      if (!email || email.trim().length === 0) {
        errors.push('Email is required');
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
          errors.push('Please enter a valid email address');
        }
      }

      return errors;
    },

    validatePassword: (password: string, confirmPassword?: string): string[] => {
      const errors: string[] = [];

      if (!password || password.length === 0) {
        errors.push('Password is required');
      } else {
        if (password.length < 8) {
          errors.push('Password must be at least 8 characters long');
        }
        if (!/(?=.*[a-z])/.test(password)) {
          errors.push('Password must contain at least one lowercase letter');
        }
        if (!/(?=.*[A-Z])/.test(password)) {
          errors.push('Password must contain at least one uppercase letter');
        }
        if (!/(?=.*\d)/.test(password)) {
          errors.push('Password must contain at least one number');
        }
        if (!(/(?=.*[@$!%*?&])/.test(password))) {
          errors.push('Password must contain at least one special character (@$!%*?&)');
        }
      }

      if (confirmPassword !== undefined && password !== confirmPassword) {
        errors.push('Passwords do not match');
      }

      return errors;
    },

    validatePaymentMethod: (paymentMethod: UpdatePaymentMethodRequest): string[] => {
      const errors: string[] = [];

      // Card Number validation
      if (!paymentMethod.CardNumber || paymentMethod.CardNumber.trim().length === 0) {
        errors.push('Card number is required');
      } else {
        const cardNumber = paymentMethod.CardNumber.replace(/\s/g, '');
        if (!/^\d{13,19}$/.test(cardNumber)) {
          errors.push('Card number must be 13-19 digits');
        }
      }

      // Expiration Month validation
      if (!paymentMethod.ExpirationMonth || paymentMethod.ExpirationMonth < 1 || paymentMethod.ExpirationMonth > 12) {
        errors.push('Expiration month must be between 1 and 12');
      }

      // Expiration Year validation
      const currentYear = new Date().getFullYear();
      if (!paymentMethod.ExpirationYear || paymentMethod.ExpirationYear < currentYear || paymentMethod.ExpirationYear > currentYear + 20) {
        errors.push(`Expiration year must be between ${currentYear} and ${currentYear + 20}`);
      }

      // Check if card is expired
      if (paymentMethod.ExpirationMonth && paymentMethod.ExpirationYear) {
        const currentDate = new Date();
        const expirationDate = new Date(paymentMethod.ExpirationYear, paymentMethod.ExpirationMonth - 1);
        if (expirationDate < currentDate) {
          errors.push('Card has expired');
        }
      }

      // CVC validation
      if (!paymentMethod.CVC || paymentMethod.CVC.trim().length === 0) {
        errors.push('CVC is required');
      } else if (!/^\d{3,4}$/.test(paymentMethod.CVC.trim())) {
        errors.push('CVC must be 3 or 4 digits');
      }

      return errors;
    },

    validateChangeEmail: (request: ChangeEmailRequest): string[] => {
      const errors: string[] = [];
      
      // Validate new email
      const emailErrors = accountApi.validation.validateEmail(request.NewEmail);
      errors.push(...emailErrors);

      // Validate current password
      if (!request.CurrentPassword || request.CurrentPassword.length === 0) {
        errors.push('Current password is required to change email');
      }

      return errors;
    },

    validateChangePassword: (request: ChangePasswordRequest): string[] => {
      const errors: string[] = [];

      // Validate current password
      if (!request.CurrentPassword || request.CurrentPassword.length === 0) {
        errors.push('Current password is required');
      }

      // Validate new password
      const passwordErrors = accountApi.validation.validatePassword(request.NewPassword, request.ConfirmPassword);
      errors.push(...passwordErrors);

      // Check if new password is different from current
      if (request.CurrentPassword && request.NewPassword && request.CurrentPassword === request.NewPassword) {
        errors.push('New password must be different from current password');
      }

      return errors;
    }
  }
};

export default accountApi; 